import React, { Ref } from 'react';
import { isFunction } from 'lodash';
import { first, noop } from 'lodash';
import styled from 'styled-components';
import { isTouchEvent } from '../utils';
import { Stroke, Tools } from '../types';

const StyledCanvas = styled.canvas`
    width: 300px;
    height: 300px;
    user-select: none;
`;

const getPropertiesForTool = (tool: Tools): Pick<Stroke, 'strokeStyle' | 'lineWidth'> => {
    if (tool === Tools.Pencil) {
        return {
            strokeStyle: 'black',
            lineWidth: 1,
        };
    } else {
        return {
            strokeStyle: 'white',
            lineWidth: 10,
        };
    }
};

export const MyCanvas = React.forwardRef<
    any,
    // @ts-ignore
    { className?: string, onDraw: (stroke: Omit<Stroke, 'id' | 'roomId'>) => void },
>(({ onDraw = noop, className }, ref) => {
    const [isDrawing, setIsDrawing] = React.useState();
    const canvas = React.useRef<HTMLCanvasElement>(null);
    const [lastX, setLastX] = React.useState();
    const [lastY, setLastY] = React.useState();
    const [tool, setTool] = React.useState<Tools>(Tools.Pencil);

    const { lineWidth, strokeStyle } = getPropertiesForTool(tool);

    const draw = ({ start, end, lineWidth, strokeStyle }: Omit<Stroke, 'id' | 'roomId'>) => {
        if (start && end) {
            if (canvas.current) {
                const ctx = canvas.current.getContext('2d');
                if (ctx) {
                    ctx.beginPath();
                    ctx.strokeStyle = strokeStyle;
                    ctx.lineWidth = lineWidth;
                    ctx.lineJoin = 'round';
                    ctx.moveTo(start[0], start[1]);
                    ctx.lineTo(end[0], end[1]);
                    ctx.closePath();
                    ctx.stroke();

                    onDraw({
                        start,
                        end,
                        strokeStyle,
                        lineWidth,
                    });
                }
            }
        }
    };

    const clear = () => {
        if (canvas.current) {
            const ctx = canvas.current.getContext('2d');
            if (ctx) {
                ctx.clearRect(0, 0, 300, 300);
            }
        }
    };

    const api = {
        draw,
        clear,
        setTool,
        // TODO: This is not working properly
        // because we are using a ref
        getActiveTool: () => tool,
    };

    if (!isFunction(ref) && ref) {
        ref.current = api;
    }

    // TODO: Support colors!
    const handleMove = (
        e: React.TouchEvent<HTMLCanvasElement> | React.MouseEvent<HTMLCanvasElement>,
    ) => {
        if (canvas.current) {
            const ctx = canvas.current.getContext('2d');
            if (ctx && isDrawing) {
                let pageX;
                let pageY;
                if (isTouchEvent(e)) {
                    ({ pageX, pageY } = first(e.touches) || {});
                } else {
                    ({ pageX, pageY } = e);
                }
                const { x, y } = canvas.current.getBoundingClientRect();

                const nextX = (pageX || 0) - x;
                const nextY = (pageY || 0) - y;

                api.draw({
                    start: [lastX, lastY],
                    end: [nextX, nextY],
                    lineWidth,
                    strokeStyle,
                });

                setLastX(nextX);
                setLastY(nextY);
            }
        }
    };

    const handleStartDrawing = (
        e: React.TouchEvent<HTMLCanvasElement> | React.MouseEvent<HTMLCanvasElement>,
    ) => {
        if (canvas.current) {
            const ctx = canvas.current.getContext('2d');
            if (ctx) {
                let pageX;
                let pageY;
                if (isTouchEvent(e)) {
                    ({ pageX, pageY } = first(e.touches) || {});
                } else {
                    ({ pageX, pageY } = e);
                }
                const { x, y } = canvas.current.getBoundingClientRect();
                const lastX = (pageX || 0) - x;
                const lastY = (pageY || 0) - y;
                setIsDrawing(true);
                setLastX(lastX);
                setLastY(lastY);

                api.draw({
                    start: [lastX - 1, lastY - 1],
                    end: [lastX, lastY],
                    lineWidth,
                    strokeStyle,
                });
            }
        }
    };

    // TODO: Change the API of this to something more flexible
    return (
        <StyledCanvas
            className={className}
            ref={canvas}
            width={300}
            height={300}
            onMouseOut={() => setIsDrawing(false)}
            onMouseDown={handleStartDrawing}
            onMouseUp={() => setIsDrawing(false)}
            onMouseMove={handleMove}
            onTouchMove={handleMove}
            onTouchStart={handleStartDrawing}
            onTouchEnd={() => setIsDrawing(false)}
        />
    );
});
